<template>
  <div
    id="calendar"
    class="max-w-xl border overflow-hidden rounded-xl relative w-full"
  >
    <div
      v-if="isLoading || calendarError"
      class="calendar-container w-full h-full flex flex-col items-center justify-center"
    >
      <div v-if="isLoading" class="spinner black relative w-5 h-5"></div>
      <div
        v-if="!isLoading && calendarError"
        class="text-xs font-medium text-red-600 cursor-pointer py-8 px-5"
        @click="$emit('onRefreshData')"
      >
        {{ calendarError || 'Error while loading the data, click to retry.' }}
      </div>
    </div>
    <client-only>
      <div v-if="!isLoading && !calendarError" class="relative">
        <div
          class="w-full py-2 text-sm text-black font-medium text-center select-none"
        >
          {{ activeMonth[activeMonth.length - 1] }}
        </div>
        <!-- button prev -->
        <button class="calendar-nav-button left-0 z-20" @click="onCalendaPrev">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="fill-current"
          >
            <path
              d="M14.7 15.3001C14.8444 15.4927 14.9146 15.7309 14.8975 15.971C14.8804 16.2111 14.7773 16.437 14.6071 16.6072C14.4369 16.7774 14.211 16.8805 13.9709 16.8976C13.7308 16.9146 13.4926 16.8445 13.3 16.7001L9.30002 12.7001C9.11679 12.5132 9.01416 12.2619 9.01416 12.0001C9.01416 11.7383 9.11679 11.487 9.30002 11.3001L13.3 7.3001C13.4926 7.15567 13.7308 7.08555 13.9709 7.10261C14.211 7.11968 14.4369 7.22278 14.6071 7.39299C14.7773 7.5632 14.8804 7.7891 14.8975 8.02921C14.9146 8.26932 14.8444 8.50753 14.7 8.7001L11.42 12.0001L14.72 15.3001H14.7Z"
            />
          </svg>
        </button>
        <!-- calendar content -->
        <div
          id="calendar-content"
          class="calendar-container overflow-scroll flex flex-row pb-0 pt-2 w-full transition-transform duration-200 hide-scrollbar"
        >
          <div
            v-for="(day, idx) in listOfDays"
            :key="`${day.text}-${idx}`"
            :data-month="day.dateMonth"
            class="calendar-day-column flex flex-col px-2 w-full"
          >
            <div
              class="text-sm font-medium text-black text-center mb-2 select-none"
              :class="{ 'text-pink-600': idx === 0 }"
            >
              {{ day.dateName }}
            </div>
            <div class="hide-scrollbar h-full overflow-scroll pb-4">
              <!-- list of availability -->
              <CalendarCell
                v-for="slot in meetingsList[day.dateName]"
                :key="slot.startsAt"
                :text="slot.formatted"
                :selected="
                  selected.findIndex((s) => s.startsAt === slot.startsAt) > -1
                "
                @onCellClicked="onClickCell(slot)"
              />
              <!-- empty state -->
              <div
                v-if="
                  !meetingsList[day.dateName] ||
                    !meetingsList[day.dateName].length
                "
                class="flex flex-col justify-center items-center h-full text-sm font-medium text-gray-500 select-none"
              >
                Unavailable
              </div>
            </div>
          </div>
        </div>
        <!-- button next -->
        <button
          class="calendar-nav-button right-0 z-20"
          @click="onCalendarNext"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="fill-current"
          >
            <path
              d="M9.30001 8.7001C9.15558 8.50753 9.08546 8.26932 9.10252 8.02921C9.11959 7.7891 9.22269 7.5632 9.3929 7.39299C9.56311 7.22278 9.78901 7.11968 10.0291 7.10261C10.2692 7.08555 10.5074 7.15567 10.7 7.3001L14.7 11.3001C14.8832 11.487 14.9859 11.7383 14.9859 12.0001C14.9859 12.2619 14.8832 12.5132 14.7 12.7001L10.7 16.7001C10.5074 16.8445 10.2692 16.9146 10.0291 16.8976C9.78901 16.8805 9.56311 16.7774 9.3929 16.6072C9.22269 16.437 9.11959 16.2111 9.10252 15.971C9.08546 15.7309 9.15558 15.4927 9.30001 15.3001L12.59 12.0001L9.29001 8.7001H9.30001Z"
            />
          </svg>
        </button>
      </div>
    </client-only>
  </div>
</template>

<script>
import { format, add } from 'date-fns';
import { formatToTimeZone } from 'date-fns-timezone';
import { scrollLeft } from '~/helpers';
import CalendarCell from '~/components/calendar/CalendarCell';

export default {
  components: {
    CalendarCell
  },

  props: {
    calendarError: {
      type: String,
      default: null,
      required: false
    },
    isLoading: {
      type: Boolean,
      default: null,
      required: true
    },
    items: {
      type: Array,
      default: null,
      required: false
    },
    selected: {
      type: Array,
      default: null,
      required: false
    },
    timezone: {
      type: String,
      default: 'Etc/GMT',
      required: false
    }
  },

  data() {
    return {
      activeMonth: []
    };
  },

  computed: {
    /**
     * Returns an array of 21 days (3 weeks) from now
     */
    listOfDays() {
      const now = new Date();
      const data = [];
      for (let index = 0; index < 21; index++) {
        const date = add(now, { days: index });
        const dateName = format(date, 'iii dd');
        const dateMonth = format(date, 'LLLL');
        data.push({ date, dateName, dateMonth });
      }
      // Once the dom is updatd
      this.$nextTick(() => {
        this.setMonthObserver();
      });
      return data;
    },

    /**
     * Returns an object with the following format:
     * {
     *    dateName: [...]
     * }
     * dateName example = 'Mon. 12'
     */
    meetingsList() {
      const data = {};
      if (this.items) {
        this.items.forEach((item) => {
          const d = new Date(item.startsAt);
          // Convert to selected timezone
          const dateName = formatToTimeZone(d, 'ddd DD', {
            timeZone: this.timezone
          });
          const formatted = formatToTimeZone(d, 'HH:mm', {
            timeZone: this.timezone
          });
          if (data[dateName] && Array.isArray(data[dateName])) {
            data[dateName].push({ ...item, formatted });
            return;
          }
          data[dateName] = [{ ...item, formatted }];
        });
      }
      return data;
    }
  },

  methods: {
    /**
     * Handle click on left arrow
     */
    onCalendaPrev() {
      this.$emit('onClickNavPrev');
      this.goTo(0);
    },

    /**
     * Handle click on right arrow
     */
    onCalendarNext() {
      this.$emit('onClickNavNext');
      this.goTo(1);
    },

    /**
     * Method to scroll to the next element
     * The getElementById is done here but should be done once the the data has loaded and the DOM is done
     */
    goTo(next) {
      const container = document.getElementById('calendar-content');
      const offsetWidth = container.offsetWidth;
      const paddingL = window
        .getComputedStyle(container)
        .getPropertyValue('padding-left');
      const paddingR = window
        .getComputedStyle(container)
        .getPropertyValue('padding-right');
      const width = offsetWidth - parseFloat(paddingL) - parseFloat(paddingR);
      if (next) {
        scrollLeft({
          element: container,
          scrollTo: container.scrollLeft + width
        });
      } else {
        scrollLeft({
          element: container,
          scrollTo: container.scrollLeft - width
        });
      }
    },

    /**
     * Handle Click on a cell
     */
    onClickCell(slot) {
      this.$emit('onClickCell', slot);
    },

    /**
     * Add intersection observer for the months
     */
    setMonthObserver() {
      if (!process.browser) {
        return;
      }
      // create an observer with options
      const observerOptions = {
        root: document.querySelector('#calendar-content'),
        rootMargin: '0px -50% 0px 0px', // 110px = width of element
        threshold: 0
      };
      const els = document.querySelectorAll('[data-month]');
      // Only add the observer on the first element of a new onth
      const monthsList = [];
      // For each date element, iterate
      els.forEach((element) => {
        const m = element.getAttribute('data-month');
        if (!monthsList.includes(m)) {
          monthsList.push(m);
          const observer = new IntersectionObserver(
            (params) => this.intersectionObserverCallback(params, m),
            observerOptions
          );
          observer.observe(element);
        }
      });
    },

    intersectionObserverCallback(elements, month) {
      const el = elements?.[0];
      if (!el) {
        return;
      }
      // If element is intersecting
      if (el.isIntersecting && !this.activeMonth.includes(month)) {
        this.activeMonth.push(month);
        return;
      }
      // If element not intersecting, check the direction
      const isCurrentMonth = el.boundingClientRect?.left < el.rootBounds?.left;
      if (!isCurrentMonth) {
        this.activeMonth = this.activeMonth.filter((m) => m !== month);
      }
    }
  }
};
</script>

<style lang="postcss" scoped>
.calendar-container {
  min-height: 180px;
  max-height: 620px;
}
.calendar-day-column {
  @apply border-r border-gray-200;
  min-width: 110px;

  &:last-child {
    @apply border-r-0;
  }
}

.calendar-nav-button {
  @apply hidden absolute bg-white py-3 rounded-sm text-black;
  top: 32px;

  @screen sm {
    @apply block;
  }

  &:focus {
    @apply outline-none;
  }
  &:active {
    @apply outline-none text-gray-600;
  }
}

.hide-scrollbar {
  -ms-overflow-style: none;
  overflow: -moz-scrollbars-none;

  &::-webkit-scrollbar {
    display: none;
    height: none;
    width: none;
  }
}
</style>
